/**
 * The single place the client learns where the API lives.
 *
 * It is a *relative* path on purpose (spec 4.3 / 23.8): the production origin
 * serves both the static shell and `/api`, and hard-coding an absolute origin
 * would break the proxy in dev, the CDN cache of the public pages and the
 * cookie-scoped session. Never construct a URL from `window.location.origin`
 * anywhere else in the app — import from here.
 */
export const API_BASE = '/api/v1';

/**
 * The interface language currently in effect, read lazily.
 *
 * i18next is imported lazily and only for this one value, which keeps the API
 * client free of a dependency on the i18n module's initialisation order: this
 * file is imported by the i18n module's consumers, and a static import the other
 * way would be a cycle. The value is read on every request rather than captured,
 * so a language switch takes effect on the next call.
 */
function activeLocale(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  // `document.documentElement.lang` is kept in step with i18next by
  // `applyDocumentLanguage` on the `languageChanged` event, so it is always the
  // locale the page is actually rendering.
  return document.documentElement.lang || undefined;
}

/**
 * A failed API call, carrying the HTTP status so callers can branch on it
 * (401 -> sign in, 402 -> quota exhausted, 404 -> "company not found" screen,
 * 429 -> rate limited) without re-parsing the response.
 *
 * `message` is always a human-readable, translatable *key or text* supplied by
 * the server; the client never surfaces a raw upstream error.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly retryable: boolean;
  readonly retryAfterSeconds: number | null;

  constructor(
    status: number,
    message: string,
    options: {
      code?: string | null;
      retryable?: boolean;
      retryAfterSeconds?: number | null;
    } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = options.code ?? null;
    // 5xx and 429 are the only statuses worth retrying; a 4xx client mistake is
    // not going to fix itself, and retrying it burns a rate-limit budget.
    this.retryable = options.retryable ?? (status >= 500 || status === 429);
    this.retryAfterSeconds = options.retryAfterSeconds ?? null;
  }

  /** `true` when the entity simply is not in any connected registry. */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403;
  }

  get isPaymentRequired(): boolean {
    return this.status === 402;
  }
}

/** Network-level failure (offline, DNS, CORS) — distinct from a typed 4xx/5xx. */
export class ApiNetworkError extends ApiError {
  constructor(message: string, cause?: unknown) {
    super(0, message, { code: 'network_error', retryable: true });
    this.name = 'ApiNetworkError';
    if (cause !== undefined) this.cause = cause;
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  /** JSON body; serialised automatically. */
  json?: unknown;
  /** Query parameters; `null`/`undefined` entries are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Abort the request after N ms. Defaults to 20s (a lookup can be slow). */
  timeoutMs?: number;
  /** Skip the JSON parse and return the raw `Response` (used for SSE / blobs). */
  raw?: boolean;
}

function buildUrl(path: string, query?: ApiFetchOptions['query']): string {
  const base = path.startsWith('http') ? path : `${API_BASE}${path}`;
  if (!query) return base;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined) continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
}

interface ErrorBody {
  message?: unknown;
  code?: unknown;
  error?: unknown;
  statusCode?: unknown;
}

/** NestJS can return `message` as a string or as an array of strings. */
function readErrorMessage(body: ErrorBody | null, status: number): string {
  if (body) {
    const raw = body.message ?? body.error;
    if (typeof raw === 'string' && raw.trim()) return raw;
    if (Array.isArray(raw) && raw.length > 0) return raw.map(String).join('; ');
  }
  return `HTTP ${status}`;
}

/**
 * Translate a server error into the user's language.
 *
 * The server answers with a stable `code` (`auth.credentials_invalid`,
 * `lookup.checksum_invalid`, …) plus an English `message`: a request made before
 * sign-in carries no reliable language signal, and the public JSON API needs a
 * readable string. The web client owns the wording, so a form error appears in
 * the language the visitor chose (section 10.2).
 *
 * A message that is *already* a translation key (`errors.network`) is resolved
 * too, which is how the network and timeout errors raised in this file are shown.
 *
 * When neither is a known key — an older server, a code added after this build —
 * the server's own message is kept, because showing a raw key to a user is worse
 * than showing an English sentence.
 */
export function translateApiError(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (error instanceof ApiError) {
    const candidates = [error.code, error.message].filter(
      (value): value is string => typeof value === 'string' && value.length > 0,
    );
    for (const candidate of candidates) {
      const translated = t(candidate, { defaultValue: '' });
      if (translated) return translated;
    }
    return error.message;
  }
  if (error instanceof Error && error.message) {
    const translated = t(error.message, { defaultValue: '' });
    return translated || error.message;
  }
  return t('errors.generic');
}

function readRetryAfter(response: Response): number | null {
  const header = response.headers.get('Retry-After');
  if (!header) return null;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return seconds;
  const date = Date.parse(header);
  if (Number.isNaN(date)) return null;
  return Math.max(0, Math.round((date - Date.now()) / 1000));
}

/**
 * Typed fetch.
 *
 * Every call in the app goes through this function: it centralises the relative
 * base URL, the JSON envelope, the timeout, `credentials: 'include'` (the
 * session is an httpOnly cookie) and the translation of every failure mode into
 * an `ApiError` a component can render.
 *
 * It deliberately does NOT redirect on 401 — the caller decides whether the
 * screen should prompt for sign-in or keep showing public data.
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { json, query, timeoutMs = 20_000, raw = false, headers, signal, ...rest } = options;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(new DOMException('timeout', 'TimeoutError')), timeoutMs);
  // Let a caller-supplied signal also cancel the request (unmount, retry).
  const onAbort = () => controller.abort(signal?.reason);
  if (signal) {
    if (signal.aborted) onAbort();
    else signal.addEventListener('abort', onAbort, { once: true });
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      ...rest,
      signal: controller.signal,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        // The active interface language. The API returns some of the copy itself
        // — block headings, risk rule titles, the risk disclaimer, error
        // messages — and it cannot guess this: `Accept-Language` describes the
        // browser, not the choice the visitor made in the header switcher
        // (section 10.1).
        ...(activeLocale() ? { 'X-Locale': activeLocale() as string } : {}),
        ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: json !== undefined ? JSON.stringify(json) : undefined,
    });
  } catch (error) {
    // A caller-initiated abort is not a network error; rethrow so the caller can
    // distinguish "user navigated away" from "the API is unreachable".
    if (signal?.aborted) throw error;
    throw new ApiNetworkError('errors.network', error);
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', onAbort);
  }

  if (raw) {
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new ApiError(response.status, readErrorMessage(body, response.status), {
        code: typeof body?.code === 'string' ? body.code : null,
        retryAfterSeconds: readRetryAfter(response),
      });
    }
    return response as unknown as T;
  }

  // 204 / empty body: nothing to parse, but the call succeeded.
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      if (response.ok) {
        // A non-JSON 200 means something in front of the API answered (proxy
        // error page, captive portal). Treat it as a failure, not as data.
        throw new ApiError(response.status, 'errors.invalidResponse', {
          code: 'invalid_response',
        });
      }
      parsed = null;
    }
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      readErrorMessage(parsed as ErrorBody | null, response.status),
      {
        code:
          parsed && typeof (parsed as ErrorBody).code === 'string'
            ? ((parsed as ErrorBody).code as string)
            : null,
        retryAfterSeconds: readRetryAfter(response),
      },
    );
  }

  return parsed as T;
}

/** Normalise any thrown value into an `ApiError` for display. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (error instanceof Error) {
    if (error.name === 'AbortError' || error.name === 'TimeoutError') {
      return new ApiError(0, 'errors.timeout', { code: 'timeout', retryable: true });
    }
    return new ApiError(0, error.message, { code: 'unknown', retryable: false });
  }
  return new ApiError(0, 'errors.unknown', { code: 'unknown', retryable: false });
}
